import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";
import { readCurrentInstallationId } from "../layouts/Admin/Firebase";

export const salesApi = createApi({
  reducerPath: "salesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) =>
      prepareAuthHeaders(headers, {
        acceptJson: true,
        contentTypeJson: true,
      }),
  }),
  tagTypes: ["Sale"],
  endpoints: (builder) => ({
    fetchSalesList: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") {
            return;
          }

          searchParams.set(key, String(value));
        });

        const queryString = searchParams.toString();
        return queryString ? `sales?${queryString}` : "sales";
      },
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: (result) =>
        (result?.items || []).map((item) => ({ type: "Sale", id: item.id })),
    }),

    searchSaleItems: builder.mutation({
      query: (body) => ({
        url: "sales/search",
        method: "POST",
        body,
      }),
      transformResponse: (res) => {
        const data = res?.success ? res.data : res ?? null;
        if (!data) {
          return {
            items: [],
            pagination: {
              total: 0,
              limit: 20,
              offset: 0,
              hasMore: false,
            },
          };
        }

        const items = Array.isArray(data.items) ? data.items : [];
        const pagination = data.pagination || {};
        const limit = Number(pagination.limit ?? 20);
        const offset = Number(pagination.offset ?? 0);
        const total = Number(pagination.total ?? items.length);

        const nextOffset = Number(
          pagination.nextOffset ?? offset + items.length
        );

        return {
          items,
          pagination: {
            total,
            limit,
            offset,
            nextOffset,
            hasMore:
              typeof pagination.hasMore === "boolean"
                ? pagination.hasMore
                : nextOffset < total,
          },
        };
      },
    }),

    fetchSaleItemsAvailability: builder.mutation({
      query: (body) => ({
        url: "sales/availability",
        method: "POST",
        body,
      }),
      transformResponse: (res) => {
        const data = res?.success ? res.data : res ?? null;
        return {
          items: Array.isArray(data?.items) ? data.items : [],
        };
      },
    }),

    scanSaleItem: builder.mutation({
      query: (body) => ({
        url: "sales/scan",
        method: "POST",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
    }),

    createSale: builder.mutation({
      query: (body) => {
        const installationId = readCurrentInstallationId();
        return {
          url: "sales",
          method: "POST",
          body,
          ...(installationId
            ? { headers: { "x-fcm-installation-id": installationId } }
            : {}),
        };
      },
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (result) =>
        result?.id ? [{ type: "Sale", id: result.id }] : [],
    }),

    fetchSaleById: builder.query({
      query: (saleId) => `sales/${saleId}`,
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: (_result, _error, saleId) => [{ type: "Sale", id: saleId }],
    }),
  }),
});

export const {
  useFetchSalesListQuery,
  useLazyFetchSalesListQuery,
  useSearchSaleItemsMutation,
  useFetchSaleItemsAvailabilityMutation,
  useScanSaleItemMutation,
  useCreateSaleMutation,
  useFetchSaleByIdQuery,
} = salesApi;
